import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and structured data
 * @param {Object} props
 * @param {string} props.title - Page title (default: "Osailu - Learn with the Future in Mind")
 * @param {string} props.description - Meta description (default: "Osailu helps you learn with the future in mind.")
 * @param {string} props.keywords - Comma-separated keywords
 * @param {string} props.image - Open Graph/Twitter Card image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - Open Graph type (default: "website")
 * @param {string} props.locale - Language locale (default: "en_US")
 * @param {Object} props.structuredData - JSON-LD structured data object
 * @param {boolean} props.noindex - Whether to prevent indexing (default: false)
 */
const SEO = ({
  title = "Osailu - Learn with the Future in Mind",
  description = "Osailu helps you learn with the future in mind.",
  keywords = "Osailu, future learning, education technology, innovative learning, forward-thinking education, learning tools",
  image = null,
  url = null,
  type = "website",
  locale = "en_US",
  structuredData = null,
  noindex = false,
}) => {
  // Get the base URL from window location or use a default
  const baseUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}` 
    : 'https://osailu.com';
  
  // Construct full URL if relative path provided
  const fullUrl = url 
    ? (url.startsWith('http') ? url : `${baseUrl}${url}`)
    : (typeof window !== 'undefined' ? window.location.href : baseUrl);
  
  // Default image if not provided
  const ogImage = image || `${baseUrl}/OsailuAndBoat.png`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  
  // Full title with site name
  const fullTitle = title.includes('Osailu') ? title : `${title} | Osailu`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Emerson Warwick Limited" />
      <meta name="application-name" content="Osailu" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content="Osailu" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:creator" content="@Osailu" />
      <meta name="twitter:site" content="@Osailu" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Osailu" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
